import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useEzioAPI } from '@ezio/services'
import { createFile, downloadFile } from '@ezio/utils'
import dayjs from 'dayjs'
import { Wallet, BankStatementRaw, BankStatementOperation } from '@ezio/features/bank/types'
import { Operations } from '@ezio/features/agencies/types'
import { localStores } from '@ezio/features'
import { ScaOperation } from 'sca/types'

export const useBankStore = defineStore('bank', () => {
    const { api } = useEzioAPI()

    const wallet = ref<Wallet>(null)
    const operations = ref<Operations>(null)
    const pendingPayins = ref<any[]>([])

    const fetch = async (agencyId?: string) => {
        const currentAgencyId = localStores.currentStore?.agencyId
        const targetAgencyId = agencyId || currentAgencyId

        if (!targetAgencyId) return

        const response = await api.get(`agencies/${targetAgencyId}/wallet`)

        // Stocker dans le state global seulement si c'est l'agence courante
        if (targetAgencyId === currentAgencyId) {
            wallet.value = response.data
        }

        return response.data
    }

    const create = async (agencyId: string) => {
        const response = await api.post(`agencies/${agencyId}/wallet`)
        return response.data
    }

    const fetchOperations = async (cursor: string = null) => {
        const agencyId = localStores.currentStore?.agencyId
        if (!agencyId) return
        const operationsData = await api.get(`agencies/${agencyId}/wallet/operations`, { cursor })
        operations.value = operationsData.data
    }
    const fetchPendingPayins = async () => {
        const agencyId = localStores.currentStore?.agencyId
        if (!agencyId) return
        const response = await api.get(`agencies/${agencyId}/wallet/pending_payins`)
        pendingPayins.value = response.data.reverse()
    }

    const fetchAccountDetails = async (agencyId: string) => {
        const report = await api.get(`agencies/${agencyId}/wallet/account_details`)
        return report.data
    }
    const fetchStatement = async (agencyId: string, month: string, year: string, file_type = 'computed') => {
        const report = await api.get(`agencies/${agencyId}/wallet/statements`, {
            month,
            year,
            file_type
        })
        if (file_type === 'raw') {
            return convertJsonToCsv(report.data)
        }
        return report.data
    }

    const convertJsonToCsv = (data: BankStatementRaw): string => {
        const BOM = '\uFEFF'
        const header = 'Date;Type;Opération;Débit;Crédit'

        const rows = data.operations.map((op: BankStatementOperation) => {
            const date = dayjs(op.date).format('DD/MM/YYYY')
            const type = op.type
            const operation = op.name.replace(/;/g, ',')
            const debit = op.direction === 'DEBIT' ? String(op.amount).replace('.', ',') : ''
            const credit = op.direction === 'CREDIT' ? String(op.amount).replace('.', ',') : ''

            return `${date};${type};${operation};${debit};${credit}`
        })

        return BOM + [header, ...rows].join('\n')
    }

    const handleValidatedScaOperation = async (operation: ScaOperation) => {
        if (operation.action === 'statement') {
            if(operation.payload.afterValidation.fileType === 'computed') {
                const response = await fetchStatement(operation.payload.afterValidation.agencyId, operation.payload.afterValidation.month, operation.payload.afterValidation.year, operation.payload.afterValidation.fileType)
                downloadFile(response.link, `releve_compte_${operation.payload.afterValidation.year}-${operation.payload.afterValidation.month}.pdf`)
            }
            else {
                const csvContent = await fetchStatement(operation.payload.afterValidation.agencyId, operation.payload.afterValidation.month, operation.payload.afterValidation.year, operation.payload.afterValidation.fileType)
                createFile({
                    filename: `releve_compte_${operation.payload.afterValidation.year}-${operation.payload.afterValidation.month}.csv`,
                    filetype: 'text/csv;charset=utf-8',
                    data: csvContent
                })
            }
        } else if (operation.action === 'account_details') {
            const response = await fetchAccountDetails(operation.payload.afterValidation.agencyId)
            downloadFile(response.link, 'RIB_compte_ezio.pdf')
        }
    }

    const reset = () => {
        wallet.value = null
        operations.value = null
        pendingPayins.value = []
    }

    return {
        wallet,
        operations,
        pendingPayins,
        fetch,
        create,
        fetchOperations,
        fetchPendingPayins,
        fetchAccountDetails,
        fetchStatement,
        handleValidatedScaOperation,
        reset
    }
})
